import { useQueries, useQuery } from '@tanstack/react-query';

import {
  analyticsAnnualReturns,
  analyticsBySymbol,
  analyticsCalendar,
  analyticsClosedLots,
  analyticsFlagImpact,
  analyticsOverview,
  analyticsSymbolLots,
  drawdownHistory,
  pnlHistory,
  portfolioMetrics,
  type AnalyticsPeriod,
} from '../../lib/api/endpoints';
import { keepPreviousDataForAccount } from '../../lib/queryRefresh';

export const analyticsKeys = {
  all: ['analytics'] as const,
  portfolio: (accountId: string | null) => [...analyticsKeys.all, 'portfolio', accountId] as const,
  drawdown: (accountId: string | null, from: string, to: string) =>
    [...analyticsKeys.all, 'drawdown', accountId, from, to] as const,
  pnl: (accountId: string | null, from: string, to: string, groupBy: string) =>
    [...analyticsKeys.all, 'pnl', accountId, from, to, groupBy] as const,
  overview: (accountId: string | null, period: AnalyticsPeriod) =>
    [...analyticsKeys.all, 'overview', accountId, period] as const,
  calendar: (accountId: string | null, year: number) =>
    [...analyticsKeys.all, 'calendar', accountId, year] as const,
  bySymbol: (accountId: string | null, period: AnalyticsPeriod) =>
    [...analyticsKeys.all, 'by-symbol', accountId, period] as const,
  symbolLots: (accountId: string | null, symbol: string | null) =>
    [...analyticsKeys.all, 'symbol-lots', accountId, symbol] as const,
  closedLots: (accountId: string | null, from: string, to: string) =>
    [...analyticsKeys.all, 'closed-lots', accountId, from, to] as const,
  flagImpact: (accountId: string | null) =>
    [...analyticsKeys.all, 'flag-impact', accountId] as const,
  annualReturns: (accountId: string) =>
    [...analyticsKeys.all, 'annual-returns', accountId] as const,
};

/** NAV-based calendar-year returns for each account, in the order given. */
export function useAnnualReturnsForAccounts(accountIds: readonly string[]) {
  return useQueries({
    queries: accountIds.map((accountId) => ({
      queryKey: analyticsKeys.annualReturns(accountId),
      queryFn: () => analyticsAnnualReturns(accountId),
    })),
  });
}

export function usePortfolioMetrics(accountId: string | null) {
  return useQuery({
    queryKey: analyticsKeys.portfolio(accountId),
    queryFn: () => portfolioMetrics(accountId ?? ''),
    enabled: Boolean(accountId),
  });
}

export function useDrawdownHistory(accountId: string | null, from: string, to: string) {
  return useQuery({
    queryKey: analyticsKeys.drawdown(accountId, from, to),
    queryFn: () => drawdownHistory({ accountId: accountId ?? '', from, to }),
    enabled: Boolean(accountId),
  });
}

export function usePnlHistory(
  accountId: string | null,
  from: string,
  to: string,
  groupBy: 'day' | 'week' | 'month',
) {
  return useQuery({
    queryKey: analyticsKeys.pnl(accountId, from, to, groupBy),
    queryFn: () => pnlHistory({ accountId: accountId ?? '', from, to, groupBy }),
    enabled: Boolean(accountId),
    placeholderData: keepPreviousDataForAccount(accountId),
  });
}

export function useAnalyticsOverview(accountId: string | null, period: AnalyticsPeriod) {
  return useQuery({
    queryKey: analyticsKeys.overview(accountId, period),
    queryFn: () => analyticsOverview({ accountId: accountId ?? '', period }),
    enabled: Boolean(accountId),
    placeholderData: keepPreviousDataForAccount(accountId),
  });
}

export function useAnalyticsCalendar(accountId: string | null, year: number) {
  return useQuery({
    queryKey: analyticsKeys.calendar(accountId, year),
    queryFn: () => analyticsCalendar({ accountId: accountId ?? '', year }),
    enabled: Boolean(accountId),
    placeholderData: keepPreviousDataForAccount(accountId),
  });
}

export function useAnalyticsBySymbol(accountId: string | null, period: AnalyticsPeriod) {
  return useQuery({
    queryKey: analyticsKeys.bySymbol(accountId, period),
    queryFn: () => analyticsBySymbol({ accountId: accountId ?? '', period }),
    enabled: Boolean(accountId),
    placeholderData: keepPreviousDataForAccount(accountId),
  });
}

export function useAnalyticsSymbolLots(accountId: string | null, symbol: string | null) {
  return useQuery({
    queryKey: analyticsKeys.symbolLots(accountId, symbol),
    queryFn: () => analyticsSymbolLots({ accountId: accountId ?? '', symbol: symbol ?? '' }),
    enabled: Boolean(accountId && symbol),
  });
}

export function useAnalyticsClosedLots(accountId: string | null, from: string, to: string) {
  return useQuery({
    queryKey: analyticsKeys.closedLots(accountId, from, to),
    queryFn: () => analyticsClosedLots({ accountId: accountId ?? '', from, to }),
    enabled: Boolean(accountId),
  });
}

export function useAnalyticsFlagImpact(accountId: string | null) {
  return useQuery({
    queryKey: analyticsKeys.flagImpact(accountId),
    queryFn: () => analyticsFlagImpact(accountId ?? ''),
    enabled: Boolean(accountId),
  });
}
